import { limits } from "@minket/core";
import { Button } from "@minket/ui/components/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { useMarkStepDone } from "@/components/onboarding/mark-done";
import { StepPage } from "@/components/onboarding/step-page";
import { Field, FormError } from "@/components/tenancy/field";
import { isApiRequestError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { sessionKey } from "@/lib/queries";
import { apiKey, apiRequest } from "@/lib/query";
import type { Membership } from "@/lib/session";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/organization")({
  component: OrganizationStep,
});

const signupClosed = "Sign-up is closed. Ask an owner of an organization to invite you.";
const teammates = {
  title: "Teammates, when you are ready",
  text: "Invite them from Team. They sign in with the address you invite.",
};

const link =
  "cursor-pointer rounded-sm text-primary underline decoration-1 underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

/**
 * The first step names the organization in view: its name ready to save (an owner can change
 * it; an admin keeps it), its slug shown as it stays. The step is done once that is saved, or
 * when the organization was created here or in Settings, which names it (the API marks it).
 * Without an organization, or to start another one, the step creates it.
 */
function OrganizationStep() {
  const { organization } = useWorkspace();
  const [creating, setCreating] = useState(false);
  if (organization && !creating) {
    return (
      <NameOrganization
        key={organization.organization_id}
        organization={organization}
        onCreate={() => setCreating(true)}
      />
    );
  }
  return (
    <CreateOrganization current={organization?.name ?? null} onKeep={() => setCreating(false)} />
  );
}

function NameOrganization({
  organization,
  onCreate,
}: {
  organization: Membership;
  onCreate: () => void;
}) {
  const canRename = useCan("org.manage");
  const markDone = useMarkStepDone("organization");
  const queryClient = useQueryClient();
  const formId = useId();
  const save = useMutation({
    mutationFn: async (name: string) => {
      if (canRename && name !== organization.name) {
        await apiRequest(
          endpoints.updateOrganization,
          { orgId: organization.organization_id },
          { body: { name } },
        );
      }
    },
    onSuccess: async () => {
      markDone();
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      notify("success", "Organization name saved.");
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name") ?? "").trim();
    save.mutate(canRename ? name : organization.name);
  };
  return (
    <StepPage
      path="/onboarding/organization"
      title="Name your organization"
      intro="An organization holds its projects, domains and team. Its name is what members and invitations show."
      asks={[
        {
          title: "A name",
          text: "The name people see. The slug, one lowercase word in paths, stays as it is.",
        },
        teammates,
      ]}
      action={
        <Button type="submit" form={formId} loading={save.isPending}>
          {canRename ? "Save name" : "Keep this name"}
        </Button>
      }
    >
      <form
        id={formId}
        onSubmit={submit}
        noValidate
        aria-label="Organization name"
        className="grid max-w-xl gap-4 sm:grid-cols-2"
      >
        <Field
          label="Name"
          name="name"
          error={save.error}
          hint={canRename ? undefined : "Only an owner can rename it."}
        >
          {(control) => (
            <input
              {...control}
              required
              readOnly={!canRename}
              maxLength={limits.orgNameMax}
              defaultValue={organization.name}
              autoComplete="off"
            />
          )}
        </Field>
        <Field label="Slug" name="slug" hint="Slugs do not change." error={null}>
          {(control) => <input {...control} readOnly defaultValue={organization.slug} />}
        </Field>
        <FormError error={save.error} className="text-sm text-destructive sm:col-span-2" />
      </form>
      <p className="text-sm text-muted-foreground">
        For a separate business,{" "}
        <button type="button" onClick={onCreate} className={link}>
          create another organization
        </button>
        .
      </p>
    </StepPage>
  );
}

function CreateOrganization({ current, onKeep }: { current: string | null; onKeep: () => void }) {
  const { selectOrganization } = useWorkspace();
  const queryClient = useQueryClient();
  const formId = useId();
  const create = useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      apiRequest(endpoints.createOrganization, {}, { body }),
    onSuccess: async (org) => {
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.onboarding) });
      selectOrganization(org.id);
      notify("success", `${org.name} created. You are its owner.`);
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      name: String(form.get("name") ?? "").trim(),
      slug: String(form.get("slug") ?? "").trim(),
    });
  };
  const created = create.data;
  const closed = isApiRequestError(create.error) && create.error.code === "signup_closed";
  return (
    <StepPage
      path="/onboarding/organization"
      title="Name your organization"
      intro="An organization holds its projects, domains and team, and sends from the moment it exists. You become its owner."
      asks={[
        {
          title: "A name and a slug",
          text: "The name people see, and one lowercase word for paths, such as auroratech.",
        },
        teammates,
      ]}
      action={
        <Button type="submit" form={formId} loading={create.isPending}>
          <PlusIcon aria-hidden />
          Create organization
        </Button>
      }
    >
      {created ? (
        <p role="status" className="text-sm wrap-anywhere">
          {created.name} is ready to send, and you are its owner.{" "}
          <Link to="/team" className="text-primary underline underline-offset-4">
            Invite teammates
          </Link>
        </p>
      ) : null}
      <form
        id={formId}
        onSubmit={submit}
        noValidate
        aria-label="New organization"
        className="grid max-w-xl gap-4 sm:grid-cols-2"
      >
        <Field label="Name" name="name" error={create.error}>
          {(control) => (
            <input {...control} required maxLength={limits.orgNameMax} autoComplete="off" />
          )}
        </Field>
        <Field label="Slug" name="slug" error={create.error}>
          {(control) => (
            <input {...control} required maxLength={limits.slugMax} autoComplete="off" />
          )}
        </Field>
        {closed ? (
          <p role="alert" className="text-sm text-destructive sm:col-span-2 wrap-anywhere">
            {signupClosed}
          </p>
        ) : (
          <FormError error={create.error} className="text-sm text-destructive sm:col-span-2" />
        )}
      </form>
      {current && !created ? (
        <p className="text-sm text-muted-foreground">
          <button type="button" onClick={onKeep} className={link}>
            Keep {current} instead
          </button>
        </p>
      ) : null}
    </StepPage>
  );
}
